import { Button } from "wiz-front-shared/components/button/Button";
import { TermCheckBox } from "wiz-front-shared/components/termCheckBox/TermCheckBox";
import { useAtom, useSetAtom } from "jotai";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { signUpDataAtom, pushStepAtom, TermsConsentItem } from "store/signUpStore";
import { userStore } from "store/userStore";
import { getStoredServiceId } from "common/app";

// 약관 데이터 타입
interface TermsItem {
    termsId: number;
    serviceId: string;
    locale: string;
    termsCode: string;
    termsCodeName: string;
    termsTitle: string;
    termsVersion: string;
    isRequired: "Y" | "N";
}

// 개별 약관 아이템 컴포넌트
const TermItem = ({
    term,
    checked,
    onCheck,
    onOpenDetail,
    termsDetail,
    isDetailLoading
}: {
    term: TermsItem;
    checked: boolean;
    onCheck: (termsId: number, checked: boolean) => void;
    onOpenDetail: (termsId: number) => void;
    termsDetail: { termsTitle?: string; termsContent?: string } | null;
    isDetailLoading: boolean;
}) => {
    const [isChecked, setIsChecked] = useState(checked);

    useEffect(() => {
        setIsChecked(checked);
    }, [checked]);

    const handleAgree = () => {
        onCheck(term.termsId, true);
    };

    return (
        <TermCheckBox
            title={term.termsTitle}
            id={`term_${term.termsId}`}
            checked={isChecked}
            setChecked={setIsChecked}
            isRequired={term.isRequired === "Y"}
            onChange={(e) => onCheck(term.termsId, e.target.checked)}
            onOpenDetail={() => onOpenDetail(term.termsId)}
            onAgree={handleAgree}
            termsTitle={termsDetail?.termsTitle}
            termsContent={termsDetail?.termsContent}
            isDetailLoading={isDetailLoading}
        />
    );
};

export const SignUpStep1 = () => {
    const {t, i18n} = useTranslation();
    const pushStep = useSetAtom(pushStepAtom);
    const [signUpData, setSignUpData] = useAtom(signUpDataAtom);
    const [termsState] = useAtom(userStore.termsList.stateAtom);
    const [termsDetailState] = useAtom(userStore.termsItem.stateAtom);

    // 약관 체크 상태 (termsId를 key로) — 진입 시 signUpData.termsConsents 로부터 복원
    //   (Step2 갔다가 device back 으로 돌아왔을 때 체크 유지)
    const [checkedTerms, setCheckedTerms] = useState<Record<number, boolean>>(() => {
        const initial: Record<number, boolean> = {};
        signUpData.termsConsents.forEach(tc => {
            initial[tc.termsId] = true;
        });
        return initial;
    });
    // 현재 상세 조회 중인 termsId
    const [currentDetailTermsId, setCurrentDetailTermsId] = useState<number | null>(null);

    // 약관 목록 조회 — 이미 ready 면 skip (재진입 시 체크박스 깜빡임 방지).
    //   locale 변경 시는 다시 fetch (deps).
    //   캐시는 '실제 항목이 있을 때'만 사용한다. 응답이 200 이어도 본문이 예상과 다르면
    //   (게이트웨이 에러 페이지 등) data 는 truthy 지만 data.data 가 비어 목록이 그려지지 않는데,
    //   이때 캐시로 판정하면 재조회가 영구히 막혀 앱을 재시작하기 전까지 빈 화면이 유지된다.
    useEffect(() => {
        const locale = i18n.language || "ko";
        const serviceId = getStoredServiceId();
        if (!serviceId) return;
        if (termsState.status === "ready" && (termsState.data?.data?.length ?? 0) > 0) return;  // 캐시 활용
        userStore.onGetTermConsent(locale, serviceId, "sign_up");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    // 약관 목록 (API 응답의 data 배열에서 추출)
    const termsList: TermsItem[] = useMemo(() => {
        return termsState.data?.data || [];
    }, [termsState.data]);

    // 필수 약관 목록
    const requiredTerms = useMemo(() => {
        return termsList.filter(term => term.isRequired === "Y");
    }, [termsList]);

    // 모든 필수 약관 동의 여부
    const allRequiredChecked = useMemo(() => {
        return requiredTerms.length > 0 && requiredTerms.every(term => checkedTerms[term.termsId]);
    }, [requiredTerms, checkedTerms]);

    // 체크박스 변경 핸들러
    const handleTermCheck = useCallback((termsId: number, checked: boolean) => {
        setCheckedTerms(prev => ({
            ...prev,
            [termsId]: checked
        }));
    }, []);

    // 약관 상세 열기 핸들러
    const handleOpenDetail = useCallback((termsId: number) => {
        setCurrentDetailTermsId(termsId);
        userStore.onGetTermsDetailConsent(String(termsId));
    }, []);

    // 현재 약관의 상세 정보
    const getTermsDetail = useCallback((termsId: number) => {
        if (currentDetailTermsId === termsId && termsDetailState.isReady) {
            return termsDetailState.data?.data || termsDetailState.data;
        }
        return null;
    }, [currentDetailTermsId, termsDetailState]);

    const handleNext = () => {
        // 동의한 약관 데이터 저장
        const termsConsents: TermsConsentItem[] = termsList
            .filter(term => checkedTerms[term.termsId])
            .map(term => ({
                termsId: term.termsId,
                termsVersion: term.termsVersion
            }));

        setSignUpData({ ...signUpData, termsConsents });

        // SNS 가입자도 Step2 를 거친다. SNS 의 birth/gender 는 카카오·네이버 계정의 임의 입력값이라
        //   다날(통신사 가입정보 대조)과 불일치할 수 있는데, 스킵하면 인증 실패 시 되돌아와 고칠 화면이
        //   없어 가입이 막힌다. prefill 로 채워주되 확인·수정은 사용자가 하도록 한다.
        pushStep(2);
    };

    return (
        <div className="sign-up-info">
            <span className="title">{t("signUp.step1.title")}</span>
            <span className="info">{t("signUp.step1.info")}</span>

            <div style={{display: "flex", flexDirection: "column", marginTop: "50px"}}>
                {termsList.map((term) => (
                    <TermItem
                        key={term.termsId}
                        term={term}
                        checked={checkedTerms[term.termsId] || false}
                        onCheck={handleTermCheck}
                        onOpenDetail={handleOpenDetail}
                        termsDetail={getTermsDetail(term.termsId)}
                        isDetailLoading={currentDetailTermsId === term.termsId && termsDetailState.isPending}
                    />
                ))}
            </div>

            <div className="sign-up-bottom-btn">
                <Button
                    text={t("signUp.btnTermsCom")}
                    onPress={handleNext}
                    disabled={!allRequiredChecked}
                />
            </div>
        </div>
    )
};
